/**
 * Game state types + live state container.
 * Types mirror corpllm-server/engine/internal/fixture/types.go.
 * State is populated via WebSocket state_update events.
 */

// ── Types (canonical source: corpllm-server/engine/internal/fixture/types.go) ─────────────────────────────

export type RunnerStatus = 'active' | 'arrested' | 'injured' | 'manager';
export type SubTier      = 'micro' | 'standard' | 'shell';
export type NotifLevel   = 'crit' | 'warn' | 'info';

export type ActionResult = 'SUCCESS' | 'FAIL' | 'CRIT';
export type ActionReportResult = 'CRIT_FAIL' | 'FAIL' | 'SUCCESS' | 'CRIT_SUCCESS' | 'ARRESTED';

export interface ActionReportEntry {
  groupId?: string;
  runner: string;
  action: string;
  actionId: string;
  target?: string;
  targetNpc?: string;
  result: ActionReportResult;
  deltaCash: number;
  deltaHeat: number;
}

export interface ActionHistoryEntry {
  day: number; action: string; target: string; result: ActionResult;
}

export type ScoutLevel = 'LOW' | 'MID' | 'HIGH';

export interface ManagerDTO {
  name: string; specialization: string; delegationCapacity: number;
  assignedRunners: string[];
}

export interface RecruitCandidate {
  idx: number;
  name: string; scoutLevel: ScoutLevel;
  str?: number; int?: number; stl?: number; tch?: number; cha?: number; con?: number;
  loyTendency?: string; skills?: string[];
  hiringCost: number;
}

/** Server-assigned visual identity for a runner. */
export interface PortraitSpec {
  model: string;         // catalog model key, e.g. "Sci_Cop"
  attachments: string[]; // attachment keys, e.g. ["SM_Chr_Attach_Hair_03"]
  skinIndex: number;     // 0..skinVariants-1
  gender: 'male' | 'female';
  pose: string;          // "idle" (renderer support planned)
}

export interface RunnerDTO {
  idx: number;
  id: string; name: string; skills: string[];
  str: number; int: number; stl: number; tch: number; cha: number; con: number;
  hp: number; heat: number; loy: number; stress: number;
  status: RunnerStatus; arrestedUntil?: number; team: string;
  homeBase?: string; // node_id of HQ or Hideout; empty = player HQ
  /** #694 — the runner's current sector (owner-only; resolved live from
   *  the pathfinding graph). Absent on static fixture snapshots. */
  sectorId?: string;
  salary: number;
  actionHistory?: ActionHistoryEntry[];
  portrait?: PortraitSpec; // server-assigned visual identity
  // HP system Phase 3 (server v1.83.0+) — per-part HP + permanent flags.
  // Owner-only; rivals see only the aggregate `hp` field above.
  body?: RunnerBodyDTO;
  // HP system Phase 4 (server v1.84.0+) — installed cyberware per body part.
  // Owner-only until Phase 4b's HACK_RECON leak lands. Empty slots omitted.
  cyberware?: CyberwareInstalledDTO[];
  // Weapons & Equipment Phase 1 (server #715) — equipped weapon + armor.
  // Owner-only; absent when the runner is unarmed and unarmored.
  loadout?: LoadoutDTO;
  // HP system Phase 5 (server v1.87.0+) — humanity 0..100 (100 = pristine).
  // Drops on cyberware INSTALL (by humanityCost), refunds 50% on REMOVE,
  // restored by THERAPY_SESSION. Band drives LOY decay multiplier +
  // Cyberpsycho behaviours (friendly-fire 10% / faction-leave roll).
  // Owner-only — rivals don't see hood humanity in the fixture.
  humanity?: number;
  humanityState?: 'stable' | 'jittery' | 'unstable' | 'cyberpsycho';
  // M4b-redux Criminal Justice — populated only on owner's own arrested runners.
  arrestSeverity?: 'petty' | 'moderate' | 'serious' | 'federal';
  arrestedSince?: number;        // day arrest started
  arrestedForAction?: string;    // action_id that triggered arrest (for flavor)
  sentenceBreakdown?: SentenceBreakdownDTO;
}

/** Weapons & Equipment Phase 1 (server #715, SPEC_weapons_equipment).
 *  Equipped weapon + armor with the combat modifier each contributes.
 *  Empty slots are omitted. Mirrors Go fixture.LoadoutDTO. */
export interface LoadoutDTO {
  weaponType?: string;     // item_type, e.g. "weapon_ranged"; absent = unarmed
  weaponName?: string;     // catalog display name
  weaponModifier?: number; // + to damage dealt
  armorType?: string;      // item_type, e.g. "armor_basic"; absent = no armor
  armorName?: string;      // catalog display name
  armorModifier?: number;  // - to incoming damage
}

/** Weapons & Equipment Phase 3 (server #717, SPEC_weapons_equipment §6.1).
 *  One stored (unequipped) weapon/armor stack in the player Armory.
 *  Owner-only. Mirrors Go fixture.ArmoryItemDTO. */
export interface ArmoryItemDTO {
  itemType: string; // item_type, e.g. "weapon_melee"
  name: string;     // catalog display name
  slot: string;     // "weapon" | "armor"
  count: number;    // how many of this item are stored
}

/** Weapons & Equipment Phase 4 (server #223, SPEC_weapons_equipment §3).
 *  One stored single-use combat item (incendiary/explosive/drone) in the
 *  player Consumable store. No slot — consumables aren't equipped. Owner-only.
 *  Mirrors Go fixture.ConsumableItemDTO. */
export interface ConsumableItemDTO {
  itemType: string; // item_type, e.g. "incendiary"
  name: string;     // catalog display name
  count: number;    // how many are stored
}

/** HP system Phase 3 — per-body-part HP + permanent flag (SPEC_combat.md §12).
 *  Six fixed slots; UI keys off the `permanent` flag for "wrecked" overlays
 *  and `permanentDay` for "since day N" tooltips. */
export interface RunnerBodyDTO {
  head: PartDTO;
  torso: PartDTO;
  leftArm: PartDTO;
  rightArm: PartDTO;
  leftLeg: PartDTO;
  rightLeg: PartDTO;
}

/** Single body-part HP state. `permanent=true` means the part is destroyed
 *  and can only be restored by HEAL_PERMANENT or INSTALL_CYBERWARE. */
export interface PartDTO {
  hp: number;
  maxHp: number;
  permanent?: boolean;
  permanentDay?: number;
}

/** HP system Phase 4 — installed cyberware slot (SPEC_combat.md §18).
 *  `partialFailure=true` marks the §18.5 bucket-2 outcome (stat_mods at 50%). */
export interface CyberwareInstalledDTO {
  cyberwareId: string;
  name: string;
  part: string;          // "head" | "torso" | "arm_left" | "arm_right" | "leg_left" | "leg_right"
  tier: number;
  statMods?: CyberwareModsDTO;
  humanityCost?: number;
  partialFailure?: boolean;
  installedDay?: number;
  ripperdocId?: string;
}

/** Signed attribute deltas applied by a cyberware install. Only non-zero
 *  fields are surfaced — undefined means "no effect on this attribute". */
export interface CyberwareModsDTO {
  str?: number;
  int?: number;
  stl?: number;
  tch?: number;
  cha?: number;
  con?: number;
}

/** M4b-redux: owner-only sentence breakdown for arrested runners. */
export interface SentenceBreakdownDTO {
  baseDays: number;
  severity: 'petty' | 'moderate' | 'serious' | 'federal';
  recidivismAdd?: number;
  judgeReduction?: number;
  mayorReduction?: number;
  finalDays: number; // -1 = federal indefinite
}

export interface HQInfo {
  nodeId: string; sectorId: string;
  worldX: number; worldY: number;
  name: string; isFront: boolean;
}

export interface TeamDTO {
  idx: number; // v1.8.2: Go required — always emitted
  name: string; members: string[]; leader: string;
  leaderCha: number; leaderInt: number; coordinationBonus: number;
  delegationCap: number;
}

export interface BuildingDTO {
  nodeId: string; name: string; type: string; sector: string;
  zone: string; // v1.8.2: Go required
  owned: boolean; income: number;
  rent: number; // v1.8.2: Go required, always emitted (0 for rent-free)
  heat: number;
  isFront: boolean; hasManager?: boolean; managerName?: string;
  propertyValue?: number; defense?: number;
  synergyTags?: string[]; storageCap?: number;
  /** v1.97.2 — canonical catalog template name ("Pharma-Lab",
   * "Chem-Refinery", "Soy-Café") regardless of any display-side
   * decoration on `name`. Use for role classification (lab / brew /
   * front / warehouse). Absent on legacy buildings pre-v1.97.2. */
  templateName?: string;
  /** v1.97.4 — production-loop signals (SPEC_economy §7.6-§7.10).
   * Owner-only on `cf.player.buildings`. Per-slot batch inventory:
   * [0]=pharma, [1]=alkohol, [2]=waffen. Lab cap=3 (or 5 for
   * Chem-Refinery), Frontbetrieb cap=2, Warehouse cap=10. Omitted
   * when fully zero. */
  goodsQueue?: [number, number, number];
  /** v1.97.4 — front carries an active SUPPLY_ILLEGAL multiplier
   * for the pharma slot (SPEC_economy §7.5). Set by SUPPLY_ILLEGAL
   * after a successful consume_inventory drain. Decays back to
   * false after `params.economy.supply_decay_days`. */
  hasSupplyPharma?: boolean;
  hasSupplyAlkohol?: boolean;
  hasSupplyWaffen?: boolean;
}

export interface SubsidiaryDTO {
  name: string; tier: SubTier; risk: number; dirty: number; frozen: boolean;
}

export interface CorpStanding {
  rank: number; name: string; netWorth: number;
  territory: number; isMe: boolean; eliminated: boolean;
  avatarUrl?: string;
}

export interface SectorSnapshot {
  sectorId: string;
  heat: number;
  owner: string | null;
  scouted?: boolean;
  /** v1.98.3 (#657) — active rival buildings in this scouted sector
   *  (non-viewer, non-eliminated). Lets the UI surface rival presence
   *  even when `owner` is null (the sector isn't a rival's HQ). */
  rivalBuildingCount?: number;
  /** #694 — active rival runners in this SCOUTED sector (runner id +
   *  owning corp only; their loadout stays fog-of-war). Mirrors Go
   *  fixture.RivalHoodSnapshot. Absent on unscouted sectors and on
   *  fixtures built without a pathfinding graph (static snapshots). */
  rivalHoods?: RivalHoodDTO[];
}

/** #694 — one visible rival runner in a scouted sector. Mirrors Go
 *  fixture.RivalHoodSnapshot. */
export interface RivalHoodDTO {
  runnerId: string;
  ownerCorp: string;
}

/** A law-enforcement unit (cop patrol, investigator, …) visible to the viewer.
 *  Server fog-filters to Local-tier units in scouted sectors (mirror of server
 *  fixture.LawUnitDTO). Drives the #213 cop_patrol target-ref sub-picker — only
 *  `cop_patrol` needs per-entity targeting; other kinds are uniquely keyed by
 *  sector/node/player. */
export interface LawUnitDTO {
  id: string;
  tier: string;
  sectorId: string;
  homeNodeId: string;
  state: string;
  hp: number;
  bribedByMe: boolean;
  dirtyCopForMe: boolean;
}

export interface NotificationDTO { level: NotifLevel; text: string; sub?: string; }

export interface EdictDTO {
  type: string; description: string; expiresDay: number;
}

export interface InstitutionDTO {
  id: string; name: string; tier: number; contacts: number; maxContacts: number;
  type?: string;
  activeEdicts?: EdictDTO[];
}

export interface ContactDTO {
  id: string; // v1.8.2: Go required, stable id "{inst}_{idx}" used for API calls
  name: string; inst: string;
  state: string; // v1.8.2: canonical payroll status — discovered | payroll | bribed | silenced (mirrors Go ContactDTO.State)
  loy: number; c3: number; cooldown: number; cost: number;
  bribeCost: number; // v1.8.2: Go required (0 when not bribable)
  // M4a Contact Drama Arcs
  daysOnPayroll?: number;    // consecutive days on Payroll (CHAIN gate: ≥30)
  chainUsed?: boolean;        // CHAIN_REFERRAL already attempted (1× per contact lifetime)
  poachCooldown?: number;     // days until next POACH_BID allowed (0 = eligible)
  pendingPoach?: PoachOfferDTO;
  /** client-only: derived UI status — 'active' for discovered-but-not-yet-on-payroll, etc.
   *  Not sent by the server. See contacts screen for derivation rules. */
  status?: 'active' | 'discovered' | 'unknown';
}

/** M4a: anonymous view of an active POACH bid. Attacker identity is NEVER exposed. */
export interface PoachOfferDTO {
  amount: number;
  openedDay: number;
  counterBid?: number;
}

export interface ElectionCandidate {
  name: string; party: string; supportPercent: number;
}

export interface ElectionDTO {
  id?: string;
  cyclesUntilElection: number;
  votePower: number;
  candidates: ElectionCandidate[];
}

export interface PlanCondition { type: string; op?: string; value: unknown; }
export interface PlanEntryDTO  { runner: string; action: string; sector: string; target?: string; targetKind?: string; targetRef?: string; team?: string; conditions: PlanCondition[]; }

export interface PrereqCondition { condition: string; }
export interface ActionEffect    { type: string; target: string; severity: string | null; attr: string[] | null; status?: string; }
export interface FailEffect      { type: string; target: string; severity: string | null; attr: string[] | null; on: string; status?: string; }

export interface ActionDTO {
  id: string; cat: string; label: string; cost: number; durationMin?: number;
  // v1.8.2 drift audit:
  // - targetType stays optional — Go field has omitempty
  // - targetOwnership/prereqState/effects/failEffects are always emitted
  //   (Go struct requires them, mapper initialises empty slice if no data)
  targetType?: string[];
  targetOwnership: string[];
  prereqState: PrereqCondition[];
  effects: ActionEffect[];
  failEffects: FailEffect[];
  allowedConditions: string[];
  conditionPresets?: Record<string, PlanCondition[]>;
  prereqMinAttr?: Record<string, number>;
  prereqSkill?: string[];
  maxPerCycle?: number;
  // Target-Polymorphie (#689) — extra target-kinds this verb accepts beyond
  // its default (e.g. BRIBE also accepts target=auditor). Drives the plan-UI
  // target picker. Mirrors server fixture.ActionDTO.Targets.
  targets?: ActionTargetDTO[];
}
// ActionTargetDTO — one verb×target-kind cell for the plan-UI picker (#689).
export interface ActionTargetDTO { kind: string; cost?: number; cooldownHours?: number; }
export interface MacroDTO      { name: string; description: string; actions: PlanEntryDTO[]; }

export interface NAPEntry     { corp: string; since: number; expires?: number | null; status?: string; secret?: boolean; }
export interface RivalEntry   { corp: string; nw: number; territory: number; heat: number; dishonored: number; }
export interface IntelEntry   { cycle: number; source: string; target: string; info: string; confirmed: boolean; }
export interface SpyEntry     { runner: string; action: string; location: string; since: number; risk: number; seen: number; }
export interface DiplomacyMsg { id: string; type: string; from: string; cycle: number; hour: string; text: string; needsDecision: boolean; status: string; to: string; }

export interface DiplomacyDTO {
  naps: NAPEntry[]; rivals: RivalEntry[]; intel: IntelEntry[];
  spies: SpyEntry[]; inbox: DiplomacyMsg[]; archive: DiplomacyMsg[];
  betrayalCount: number;
  dishonoredUntil: number; // cycles remaining (0 = not dishonored)
}

export interface StorageDTO    { name: string; usedPercent: number; capacityUnits: number; }
export interface AssetSegment  { key: string; value: number; }
/**
 * One detail row inside a BalanceCategory. Server DTO v2 (v1.10.1+).
 *
 * labelI18n is set for catalog-known entries (action aggregates — key
 * "action.<cmd>.label"). label is set for runtime names (building/runner
 * display names). targetNodeId + actionCommand let the economy screen
 * deep-link into the map / planning UI.
 */
export interface BalanceDetail {
  label?: string;
  labelI18n?: string;
  amount: number;
  count?: number;
  targetNodeId?: string;
  actionCommand?: string;
}

/**
 * One income or expense category in the daily balance. Metadata comes
 * from the server-side economy_registry.go; client renders generically
 * from legal flag (clean/dirty bucket), subsystem (collapse-expand
 * grouping), and order (sort within bucket).
 */
export interface BalanceCategory {
  key: string;
  labelI18n: string;
  amount: number;
  legal: boolean;
  subsystem: string;        // "passive" | "active" | "other"
  order: number;
  details?: BalanceDetail[];
}

export interface DailyBalanceDTO {
  income: BalanceCategory[];
  expenses: BalanceCategory[];
  cycleDay: number;
}

export interface PlayerSnapshot {
  corpName: string; cycle: number; cycleMax: number;
  cleanCash: number; dirtyCash: number; netWorth: number; federalHeat: number;
  runners: RunnerDTO[];
  teams: TeamDTO[]; // v1.8.2: Go required, always emitted (empty array when no teams)
  managers?: ManagerDTO[]; recruitPool?: RecruitCandidate[];
  buildings: BuildingDTO[];
  subs: SubsidiaryDTO[]; vehicles: string[];
  // Weapons & Equipment Phase 3 (server #717) — owner-only Armory: stored
  // (unequipped) weapons/armor + current capacity. Absent when nothing stored.
  armory?: ArmoryItemDTO[];
  armoryCapacity?: number;
  // Weapons & Equipment Phase 4 (server #223) — owner-only single-use combat
  // items + their separate capacity. Absent when nothing is stored.
  consumables?: ConsumableItemDTO[];
  consumableCapacity?: number;
  institutions?: InstitutionDTO[];
  contacts?: ContactDTO[];
  actions?: ActionDTO[];
  plan?: PlanEntryDTO[];
  planAutoRepeated?: boolean;
  macros?: MacroDTO[];
  diplomacy?: DiplomacyDTO;
  storage?: StorageDTO[];
  dailyBalance?: DailyBalanceDTO;
  netWorthHistory?: number[];
  assetBreakdown?: AssetSegment[];
  hq?: HQInfo;
  hideouts?: HQInfo[];
  scoutedSectors?: string[];
  actionReport?: ActionReportEntry[];
  /** Buildings the player is actively extorting — server v1.11.0+. Empty
   *  or missing when no extortions active. Drives map overlay, economy
   *  tribute panel, and COLLECT action filter in the planning UI. */
  extortedBuildings?: ExtortedBuildingDTO[];
  /** Ripperdocs the player has discovered — HP system Phase 4 (server
   *  v1.84.0+). Filtered per-player; rival discoveries stay hidden.
   *  Drives the Runner detail Cyberware tab + INSTALL_CYBERWARE flow. */
  ripperdocs?: RipperdocDTO[];
}

/** One discovered ripperdoc — Phase 4 SPEC_combat.md §17. */
export interface RipperdocDTO {
  id: string;
  name: string;
  nodeId: string;
  sectorId: string;
  tier: number;
  surgerySkill: number;
  /** Phase 4b — requester's effective reputation with this doc (0..100,
   *  default 50). Drives the install price discount (±25% swing) and
   *  success multiplier (±10% swing). Per-player; rivals never see. */
  reputation?: number;
  /** Phase 4d — canonical lowercase faction enum string
   *  ("independent" | "cartel" | "chromewerks" | "arasaka_med").
   *  ChromeWerks docs apply a ×0.9 multiplicative install discount;
   *  Cartel docs spawn with higher Discretion (no active effect yet,
   *  Phase 4c federal_heat trail consumes it); Arasaka Med carries
   *  label only in 4d (refusal mechanism lands in Phase 4c). */
  faction?: 'independent' | 'cartel' | 'chromewerks' | 'arasaka_med';
  inventory?: RipperdocInventoryEntryDTO[];
}

/** One stocked cyberware item at a ripperdoc. `targetParts` lists which
 *  body-part slots accept this chrome. */
export interface RipperdocInventoryEntryDTO {
  cyberwareId: string;
  name: string;
  tier: number;
  baseCost: number;
  targetParts: string[];
  statMods?: CyberwareModsDTO;
  humanityCost?: number;
}

/** One active extortion on a single building — surfaced read-only from
 *  GameState.BuildingExtortions via the fixture mapper. Mutation happens
 *  via EXTORT/COLLECT actions, state clears via end-of-day expiry. */
export interface ExtortedBuildingDTO {
  nodeId: string;
  name?: string;
  sectorId?: string;
  startDay: number;
  expiresAt: number;
  lastCollected: number;
  dailyTribute: number;   // rent × 20% — what COLLECT pays
  rent: number;
}

export interface GazetteEntry {
  headline: string; category: string; date: string;
  lead: string; body: string; impact: string; type: string;
  // Phase 3 M1 Crisis Events + Reactive Reports (all optional).
  crisisId?: string;
  eventId?: string;
  deadlineDay?: number;
  counterActions?: string[];
  effectPreview?: string;
  responded?: boolean;
  resolved?: boolean;
  isReport?: boolean;
  /** i18n interpolation values ({corp}, {sector}) for the headline/body keys.
   *  Mirrors the server GazetteEntry.HeadlineVars (ActiveCrisis.Context). Pass
   *  to t(headline, headlineVars) so "…in Sektor {sector}" resolves. Absent on
   *  fog-of-war entries by design. */
  headlineVars?: Record<string, string>;
}

export interface ClientFixture {
  generatedAt: string; engineVersion: string;
  day: number; cycleMax: number;
  deadlineHour?: number;
  dominanceThresholdPct?: number;
  player: PlayerSnapshot;
  leaderboard: CorpStanding[];
  sectors: SectorSnapshot[];
  /** Sector-id → owning corp name (null if unowned, undefined if fog-of-war hidden).
   *  Server type: map[string]*string json:"sectorOwnership" — always serialized. */
  sectorOwnership: Record<string, string | null>;
  /** #213 — law-enforcement units visible to the viewer (server fog-filters to
   *  Local tier in scouted sectors). Optional: older snapshots omit it.
   *  Powers the cop_patrol target-ref sub-picker in plan-ui. */
  lawUnits?: LawUnitDTO[];
  // v1.8.2 drift audit:
  // - notifications: Go field has no omitempty, mapper now initialises with []
  //   (previously nil slice → JSON null). Safe to require.
  // - gazette: Go required + always built by mapper via buildGazette.
  // - election: client-only UI scaffolding, no Go counterpart. Kept optional.
  notifications: NotificationDTO[];
  gazette: GazetteEntry[];
  election?: ElectionDTO;
  // Readiness (player-driven tick resolution)
  readyPlayers: string[];   // corp names of ready players
  totalPlayers: number;     // total players in session
  playerReady: boolean;     // whether THIS player is ready
  lastTickAt?: string;      // ISO timestamp of last tick
}

// ── Live state container ────────────────────────────────────────────────────

/** Populated by WS state_update before first render. Use this in screen renderers. */
export let fixture: ClientFixture = {} as ClientFixture;

/** Update the fixture reference from WebSocket state_update. */
export function setFixture(data: ClientFixture): void {
  fixture = data;
}

// ── Per-session map data (city_graph + world_buildings) ──────────────────

export interface MapData {
  cityGraph: import('../map/types').CityGraph;
  worldBuildings: Array<{
    node_id: string; sector_id: string; display_name: string;
    income_base: number; rent: number; buy_price: number;
    defense_value: number; category: string; zone: string;
    illegal: boolean; is_front_company: boolean; rentable: boolean;
    synergy_tags: string[]; facing: string; storage_cap: number;
    extort_rate: [number, number]; is_hq_slot: boolean;
    footprint?: [number, number]; footprint_cells?: string[];
    floors?: number;
  }>;
}

/** Per-session map data, populated by WS map_data event. */
export let mapData: MapData | null = null;

/** Update map data from WebSocket map_data message. */
export function setMapData(data: MapData): void {
  // Normalize: unscouted sectors arrive with nodes/edges = null (Go nil → JSON null)
  if (data?.cityGraph?.sectors) {
    for (const s of data.cityGraph.sectors) {
      if (!s.nodes) s.nodes = [];
      if (!s.edges) s.edges = [];
    }
  }
  mapData = data;
}
